import { Component, OnInit } from '@angular/core';
import { User, ErrCode } from '../../shared/models';
import { LoggerService } from '../../core/logger.service';
import { MultiAdminService } from '../../core/multiadmin.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ValidateTokenOutput } from '../../shared/models/multiadmin.model';
import { UserService } from 'src/app/core/user.service';
import { environment } from 'src/environments/environment';
import { SyncCookieService } from 'src/app/core/sync-cookie.service';
import { BlendService } from 'src/app/shared/services/blend.service';

enum Page {
    transferAccount = 'transferAccount',
    notFound = 'notFound',
    twoFactor = 'twoFactor',
}

@Component({
    templateUrl: './user-invite-consent.component.html',
    styleUrls: ['./user-invite-consent.component.scss'],
    providers: [MultiAdminService],
})
export class UserInviteConsentComponent implements OnInit {
    private confirmcode: string;
    protected errcode: ErrCode;
    protected spinner = false;
    protected reaction: boolean;
    protected isInviteChecked = false;
    private isInviteValid = false;
    protected declineSuccess = false;
    protected acceptSuccess = false;
    private isPaid = false;
    private isLegacyToCNC = false;
    private tokenData: ValidateTokenOutput;
    protected show2FA: boolean;
    protected password = '';
    protected twofacode = '';
    protected questions = {};
    public year: number = new Date().getFullYear();

    constructor(
        private log: LoggerService,
        private maService: MultiAdminService,
        private route: ActivatedRoute,
        private syncCookieService: SyncCookieService,
        private blendService: BlendService
    ) { }

    ngOnInit() {
        this.confirmcode = this.route.snapshot.queryParams['c'];
        if (this.confirmcode) {
            this.verifyEmailInvite();
        } else {
            this.errcode = new ErrCode(8103, 'No confirmation code provided');
        }
    }

    private async verifyEmailInvite() {
        this.spinner = true;
        try {
            this.tokenData = await this.maService.verifyEmailInvite(this.confirmcode);

            this.isInviteValid = this.tokenData.success ? true : false;
            this.isPaid = this.tokenData.isPro;
            this.isLegacyToCNC = this.tokenData.isLegacyToCNC === true ? true : false ;
        } catch (ex) {
            this.log.error(ex);
            this.errcode = ErrCode.fromException(ex);
        }
        this.isInviteChecked = true;
        this.spinner = false;
    }

    protected async userReaction(accepted: boolean) {
        this.reaction = accepted;
        this.errcode = null;
        this.spinner = true;
        if (accepted) {
            const pwd = this.password;
            const twofacode = this.twofacode;
            const isLegacyToCNC = this.isLegacyToCNC;
            this.password = '';
            this.twofacode = '';
            try {
                const result = await this.maService.handleAcceptedInviteConsent(this.tokenData, this.confirmcode, pwd, twofacode, isLegacyToCNC);
                if (result.success) {
                    this.acceptSuccess = true;
                } else {
                    this.isInviteValid = false;
                }
            } catch (ex) {
                delete this.reaction;
                if (ex.code === 8022) {
                    this.show2FA = true;
                } else {
                    this.log.error(ex);
                    this.errcode = ErrCode.fromException(ex);
                }
            }
            this.spinner = false;
        } else {
            try {
                const result = await this.maService.handleDeclinedInviteConsent(this.tokenData.user_id);
                if (result.success) {
                    this.declineSuccess = true;
                } else {
                    this.isInviteValid = false;
                }
            } catch (ex) {
                delete this.reaction;
                this.log.error(ex);
                this.errcode = ErrCode.fromException(ex);
            }
            this.spinner = false;
        }
    }

    protected get inviter() {
        return this.tokenData?.currentUserEmail;
    }

    protected get invitee() {
        return this.tokenData?.email;
    }

    protected get canAccept() {
        return this.password;
    }

    protected get page(): 'transfer-account' | 'not-found' | 'two-factor' {
        if (this.isInviteChecked && !this.isInviteValid) {
            return 'not-found';
        }
        else if (this.show2FA && !this.spinner) {
            return 'two-factor';
        }
        else {
            return 'transfer-account';
        }
    }

    protected get transferAccountPage(): 'invited' | 'accepted' | 'declined' {
        if (!this.acceptSuccess && !this.declineSuccess && this.isInviteChecked && !this.spinner) {
            return 'invited';
        }
        else if (this.acceptSuccess) {
            return 'accepted';
        }
        else if (this.declineSuccess) {
            return 'declined';
        }
    }

    // UI handlers
    protected onClickAccept = () => {
        this.userReaction(true);
    }

    protected onClickDecline = () => {
        this.userReaction(false);
    }

    protected toggleQuestion = (question: string) => {
        this.questions[question] = !this.questions[question];
    }
}
